import React from 'react';
import {Autocomplete, TextField, Chip} from '@mui/material';
import {VariableSizeList, ListChildComponentProps} from 'react-window';
import {
  ClientStatusCardViewModel
} from '@finpay-development/shared-platform/lib/esm/shared/model/client-status-card';


interface Client {
  clientId: string;
  clientName: string;
}

interface ClientSelectProps {
  options: ClientStatusCardViewModel[]
  value: any[];
  onChange: (event: React.SyntheticEvent, newValue: Client[]) => void;
  maxSelections?: number;
}

const VirtualizedListbox = React.forwardRef<HTMLDivElement, any>(
    function ListboxComponent(props, ref) {
      const {children, ...other} = props;
      const itemData = children as React.ReactElement[];
      const itemCount = itemData.length;
      const itemSize = 36;

      return (
          <div ref={ref} {...other}>
            <VariableSizeList
                height={300}
                width="100%"
                itemSize={() => itemSize}
                itemCount={itemCount}
                overscanCount={5}
                innerElementType="ul"
            >
              {({index, style}: ListChildComponentProps) =>
                  React.cloneElement(itemData[index], {style})
              }
            </VariableSizeList>
          </div>
      );
    }
);

export const ALL_OPTION: Client = {clientId: 'all', clientName: 'All Clients'};

export const ClientSelect: React.FC<ClientSelectProps> = ({
                                                     options,
                                                     value,
                                                     onChange,
                                                     maxSelections,
                                                   }) => {

  const handleChange = (event: React.SyntheticEvent, selected: Client[]) => {

    const added = selected.filter((item) => !value.includes(item));

    if (added?.[0]?.clientId === 'all') {
      onChange(event, [ALL_OPTION]);
    } else {
      const filteredSelection = selected.filter(
          option => option.clientId !== 'all'
      );
      
      // If we exceed maxSelections, keep only the last maxSelections items (override oldest)
      if (maxSelections && filteredSelection.length > maxSelections) {
        const limitedSelection = filteredSelection.slice(-maxSelections);
        onChange(event, limitedSelection);
      } else {
        onChange(event, filteredSelection);
      }
    }
  };

  return (
      <Autocomplete
          multiple
          disableListWrap
          ListboxComponent={VirtualizedListbox}
          options={options}
          test-id="client-select-input"
          getOptionLabel={option => option.clientName}
          value={value}
          renderInput={params => (
              <TextField
                  {...params}
                  placeholder="Search Client"
                  label="Select Client"
                  variant="outlined"
                  InputLabelProps={{shrink: true}}
              />
          )}
          renderTags={(selected, getTagProps) => {
            // If "All" is selected, show only "All"
            if (selected.length === 1 && selected[0].clientId === 'all') {
              return (
                  <Chip
                      label="All Clients"
                      {...getTagProps({index: 0})}
                      key="all"
                  />
              );
            }
            // Otherwise, show regular selected chips
            return selected.map((option, index) => (
                <Chip
                    label={option.clientName}
                    {...getTagProps({index})}
                    key={option.clientId}
                />
            ));
          }}
          onChange={handleChange}
      />
  );
};
